// Bella Vita frontend — versioned, fail-safe persistence helper.
//
// Wraps sessionStorage/localStorage with:
//  - an explicit schema version stamped into every record
//  - safe reads: corrupted JSON, wrong version, or a failed validate() all
//    resolve to `null` (never throw, never hand back stale/garbage state)
//  - storage-unavailable tolerance (private mode, quota, disabled)

/**
 * @param {object} opts
 * @param {string} opts.key            storage key
 * @param {number} opts.version        current schema version
 * @param {'session'|'local'} [opts.area='session']
 * @param {(data:any)=>boolean} [opts.validate]  extra shape check
 */
export function createPersistedSlot({ key, version, area = 'session', validate }) {
  const store = () => (area === 'local' ? window.localStorage : window.sessionStorage);

  function read() {
    let raw;
    try {
      raw = store().getItem(key);
    } catch {
      return null;
    }
    if (!raw) return null;
    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch {
      clear();
      return null;
    }
    if (!parsed || typeof parsed !== 'object' || parsed.__v !== version) {
      clear(); // stale / unknown schema -> discard
      return null;
    }
    if (validate && !safe(() => validate(parsed.data))) {
      clear();
      return null;
    }
    return parsed.data ?? null;
  }

  function write(data) {
    try {
      store().setItem(key, JSON.stringify({ __v: version, at: Date.now(), data }));
      return true;
    } catch {
      return false;
    }
  }

  function clear() {
    try {
      store().removeItem(key);
    } catch {
      /* ignore */
    }
  }

  return { read, write, clear, key, version };
}

function safe(fn) {
  try {
    return fn();
  } catch {
    return false;
  }
}
